import { AGENTS, CUSTOMER_OBJECT, ROLE_INVITATIONS } from "@/api/apiRoute";
import { authData } from "@/api/apiService";
import { handleApiResponse, handleError } from "@/utils/common";
import { errorMessage } from "@/utils/errorMessages";
import { defineStore } from "pinia";
import { useSecurityStore } from "@/stores/securityStore";

export const useEstatePlanStore = defineStore("estatePlanStore", {
  state: () => ({
    errorDisplayDuration: 4000,
    errorMsg: "",
    loading: false,
    isAuthenticateCodeModalOpen: false,
    agentCodeVerifyRes: null,
    agentCodeGenerationRes: null,
    isSetAgentPrincipleModalOpen: false,
    isSendRequestAgentPrincipleModalOpen: false,
    isRequestSentSuccessAgentModalOpen: false,
    isAgentAuthenticateModalOpen: false,
    isAgentGenerateCodeModalOpen: false,
    isAgentValidateModalOpen: false,
    isCreateAgentModalOpen: false,
    beneficiariesCanBeAgentsList: [],
    agentsList: [],
    inviteForRoleRes: null,
    invitationList: null,
    isSetExecutorPrincipleModalOpen: false,
    isSendRequestExecutorPrincipleModalOpen: false,
    isRequestSentSuccessExecutorModalOpen: false,
    isSetPrincipleGuardianModalOpen: false,
    isSendRequestGuardianModalOpen: false,
    isRequestSentSuccessGuardianModalOpen: false,
    isSetPrincipleTrusteeModalOpen: false,
    isSendRequestTrusteeModalOpen: false,
    isRequestSentSuccessTrusteeModalOpen: false,
    isCreateGuardianModalOpen: false,
    isCreateTrusteeModalOpen: false,
    isCreateExecutorModalOpen: false,
    isCreatePetModalOpen: false,
    isPetTrustSuccessModalOpen: false,
    isCreateDynamicFormModelOpen: false,
    beneficiaryList: [],
    customersList: [],
    canBeBequestBeneficiariesList: [],
    // Add caching state
    canBeBequestBeneficiariesLoaded: false,
    canBeBequestBeneficiariesLoading: false,
    beneficiariesCanBeAgentsLoaded: false,
    beneficiariesCanBeAgentsLoading: false,
    successActionType: "create",
    isAuthenticationSuccessAgentModalOpen: false,
    createFormRequestedLevel: null,
    createFormStage: null,
    lastCreatedHealthObject: null,
    OTP: null,
    isAgentAuthenticateModalOpenqq: false,
    agentCodeVerifyError: "",
  }),

  actions: {
    clearErrorMessage() {
      this.errorMsg = "";
    },

    closeDynamicForm() {
      this.isCreateDynamicFormModelOpen = false;
    },

    setErrorMessage(message) {
      this.errorMsg = errorMessage[message] || message || "An error occurred";
    },

    showError(error) {
      this.setErrorMessage(error?.response?.data?.message);
      setTimeout(() => {
        this.clearErrorMessage();
      }, this.errorDisplayDuration);
      handleError(error);
    },

    async deleteAgent(id) {
      this.loading = true;
      try {
        const response = await authData.delete(`${AGENTS}/${id}`);

        await this.getListAgents(); // Fixed: added await
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getListAgents() {
      this.loading = true;
      try {
        const response = await authData.get(AGENTS);
        this.agentsList = response.data.data || [];
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Fixed: Add caching logic
    async getBeneficiariesCanBeAgents(forceRefresh = false) {
      // Return cached data if available and not forcing refresh
      if (this.beneficiariesCanBeAgentsLoaded && !forceRefresh) {
        return { data: { data: this.beneficiariesCanBeAgentsList } };
      }

      // Prevent multiple simultaneous calls
      if (this.beneficiariesCanBeAgentsLoading) {
        // Wait for the ongoing request
        while (this.beneficiariesCanBeAgentsLoading) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return { data: { data: this.beneficiariesCanBeAgentsList } };
      }

      this.beneficiariesCanBeAgentsLoading = true;
      this.loading = true;

      try {
        const response = await authData.get(
          `${CUSTOMER_OBJECT}?includeFields=yes&canBeAgent=yes`
        );
        this.beneficiariesCanBeAgentsList = response.data.data || [];
        this.beneficiariesCanBeAgentsLoaded = true;
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
        this.beneficiariesCanBeAgentsLoading = false;
      }
    },

    // Fixed: Add caching logic
    async getCanBeBequestBeneficiaries(forceRefresh = false) {
      // Return cached data if available and not forcing refresh
      if (this.canBeBequestBeneficiariesLoaded && !forceRefresh) {
        return { data: { data: this.canBeBequestBeneficiariesList } };
      }

      // Prevent multiple simultaneous calls
      if (this.canBeBequestBeneficiariesLoading) {
        // Wait for the ongoing request
        while (this.canBeBequestBeneficiariesLoading) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return { data: { data: this.canBeBequestBeneficiariesList } };
      }

      this.canBeBequestBeneficiariesLoading = true;
      this.loading = true;

      try {
        const response = await authData.get(
          `${CUSTOMER_OBJECT}?includeFields=yes&canBeBequestTarget=yes`
        );
        this.canBeBequestBeneficiariesList = response.data.data || [];
        this.canBeBequestBeneficiariesLoaded = true;
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
        this.canBeBequestBeneficiariesLoading = false;
      }
    },

    // Method to refresh cache when needed
    async refreshCanBeBequestBeneficiaries() {
      return await this.getCanBeBequestBeneficiaries(true);
    },

    // Method to refresh cache when needed
    async refreshBeneficiariesCanBeAgents() {
      return await this.getBeneficiariesCanBeAgents(true);
    },

    async getCustomersList(query) {
      this.loading = true;
      try {
        const response = await authData.get(`${CUSTOMER_OBJECT}${query}`);
        this.customersList = response.data.data || [];
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getBeneficiaries(query) {
      this.loading = true;
      if (this.beneficiaryList.length) {
        this.loading = false;
        return this.beneficiaryList;
      }
      try {
        const response = await authData.get(`${CUSTOMER_OBJECT}${query}`);
        this.beneficiaryList = response.data.data || [];
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async beginAgentVerification(id) {
      this.loading = true;
      try {
        const response = await authData.post(`${AGENTS}/${id}/verification`);
        this.agentCodeGenerationRes = response.data.data || null;
        this.OTP = this.agentCodeGenerationRes?.code
          ? String(this.agentCodeGenerationRes.code)
          : null;
        return handleApiResponse(response);
      } catch (error) {
        console.error("API Error:", error);
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async agentCodeVerify(id, data) {
      this.loading = true;
      this.agentCodeVerifyError = "";
      try {
        const response = await authData.post(
          `me/agents/${id}/verification/verify`,
          data
        );
        this.agentCodeVerifyRes = response.data.data || [];
        this.OTP = this.agentCodeVerifyRes?.code;
        // When the owner completes verification, the agent is now accepted.
        // Wrap the current vault key for them so the handoff is armed (best
        // effort; silently defers if the owner isn't unlocked). See securityStore.
        if (this.agentCodeVerifyRes?.verified === true) {
          useSecurityStore().wrapVaultKeyForAgent(id);
        }
        return handleApiResponse(response);
      } catch (error) {
        const errorKey = error.response?.data?.message || "Invalid code";
        this.agentCodeVerifyError = errorKey;
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async inviteForRole(payload) {
      this.loading = true;
      try {
        const response = await authData.post(ROLE_INVITATIONS, payload);
        this.inviteForRoleRes = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async reInviteForRole(id) {
      this.loading = true;
      try {
        const response = await authData.post(
          `${ROLE_INVITATIONS}/${id}/reinvite`
        );
        this.inviteForRoleRes = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async deleteInvitation(id) {
      this.loading = true;
      try {
        const response = await authData.delete(`${ROLE_INVITATIONS}/${id}`);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Helper method to clear cache when data changes
    clearBeneficiariesCache() {
      this.canBeBequestBeneficiariesLoaded = false;
      this.beneficiariesCanBeAgentsLoaded = false;
      this.canBeBequestBeneficiariesList = [];
      this.beneficiariesCanBeAgentsList = [];
    },
  },
});
